import type { FlowMediaSettingsUpdatedNotification } from '@/types/flows/flow-media-settings-updated.notification';
import type { MediaPlaylistUpdatedNotification } from '@/types/flows/media-playlist-updated.notification';
import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFlowSocket } from '@/hooks/use-flow-socket';
import { patchFlowMediaSettingsEverywhere } from '@/state/mutations/media/patch-media-settings-cache';
import { patchMediaPlaylistInQueryCache } from '@/state/mutations/media/patch-media-playlist-cache';

/**
 * Keeps playlist media + virtual media rows in sync across tabs/views without waiting for GET_FLOW.
 * Joins the flow socket room from FlowDetail (not only the schedule slot list).
 */
export function useFlowDetailMediaSettingsSync({
  flowId,
  eventSlotId,
}: {
  flowId?: string;
  eventSlotId?: string | null;
}) {
  const queryClient = useQueryClient();
  const lastJoinRef = useRef<{ key: string; socketId?: string }>({ key: '' });

  const handler = useMemo(
    () => ({
      onFlowUpdated: () => undefined,
      onFlowInvalidated: () => undefined,
      onFlowMediaSettingsUpdated: (notification: FlowMediaSettingsUpdatedNotification) => {
        patchFlowMediaSettingsEverywhere(
          queryClient,
          notification.originId,
          notification.mediaSettings,
          notification.eventOriginId,
        );
      },
      onMediaPlaylistUpdated: (notification: MediaPlaylistUpdatedNotification) => {
        if (notification.playlist) {
          patchMediaPlaylistInQueryCache(queryClient, notification.playlist);
        }
      },
    }),
    [queryClient],
  );

  const { socket, isConnected } = useFlowSocket(flowId ?? '', handler);

  useEffect(() => {
    if (!socket || !flowId || !isConnected) {
      return;
    }
    const joinKey = `${flowId}:${eventSlotId ?? ''}`;
    const socketId = socket.id;
    if (lastJoinRef.current.key === joinKey && lastJoinRef.current.socketId === socketId) {
      return;
    }
    lastJoinRef.current = { key: joinKey, socketId };
    socket.emit('flowJoinRoom', { flowId, eventSlotId: eventSlotId || undefined });
  }, [socket, flowId, isConnected, eventSlotId]);
}
